import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { Modal, Spinner, Table } from "react-bootstrap";
import Select from "react-select";
import { useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { baseUrl } from "../../../services/config";
import { PremissionChecker } from "../../../services/PremissionChecker";

function CRMAdmissionTestAttempts({
  inquiry_id,
  getInquiries,
  closeCRMModal,
  attemptNo,
}) {
  const emptyAttempt = {
    inquiry_id: inquiry_id || "",
    campus_id: "",
    attempt_no: attemptNo !== undefined ? attemptNo + 1 : 1,
    test_date: "",
    test_time: "",
    result_status: "",
    remarks: "",
  };

  const alert = useAlert();
  const user = useSelector((state) => state.user.user);
  const authtoken = localStorage.getItem("userToken");
  const [attempts, setAttempts] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [resultStatuses, setResultStatuses] = useState([]);
  const campuses = user.campuses || [];
  const [form, setForm] = useState(emptyAttempt);
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const showErrors = useCallback(
    (data, fallback) => {
      const message = data?.metadata?.message;
      if (message && typeof message === "object") {
        Object.values(message).forEach((item) => alert.error(item));
      } else {
        alert.error(message || fallback);
      }
    },
    [alert],
  );

  const getAttempts = useCallback(
    async (inquiry_id) => {
      setLoading(true);
      try {
        const response = await fetch(
          `${baseUrl}/crm-admission-test-attempts/inquiry/${inquiry_id}`,
          {
            headers: { Authorization: `Bearer ${authtoken}` },
          },
        );
        const data = await response.json();
        if (!response.ok || data?.metadata?.success === false) {
          showErrors(data, "Unable to load CRM assessments");
          return;
        }
        setAttempts(data?.payload || []);
      } catch (error) {
        alert.error("Unable to load CRM assessments");
      } finally {
        setLoading(false);
      }
    },
    [alert, authtoken, showErrors],
  );

  const getOptions = useCallback(async () => {
    const headers = { Authorization: `Bearer ${authtoken}` };
    try {
      const [inquiryResponse, resultStatusResponse] = await Promise.all([
        fetch(`${baseUrl}/crm-inquiries`, { headers }),
        fetch(`${baseUrl}/crm-result-statuses`, { headers }),
      ]);
      const [inquiryData, resultStatusData] = await Promise.all([
        inquiryResponse.json(),
        resultStatusResponse.json(),
      ]);
      if (inquiryResponse.ok && inquiryData?.metadata?.success !== false) {
        setInquiries(inquiryData?.payload || []);
      }
      if (
        resultStatusResponse.ok &&
        resultStatusData?.metadata?.success !== false
      ) {
        setResultStatuses(resultStatusData?.payload || []);
      }
    } catch (error) {
      alert.error("Unable to load sssessment form options");
    }
  }, [alert, authtoken]);

  useEffect(() => {
    inquiry_id && getAttempts(inquiry_id);
    getOptions();
  }, [getAttempts, getOptions, inquiry_id]);

  const inquiryOptions = useMemo(
    () =>
      inquiries.map((inquiry) => ({
        value: inquiry.id,
        label: `${inquiry.inquiry_no ||
          `Inquiry #${inquiry.id}`} - ${inquiry.student_name ||
          "Unknown student"}`,
      })),
    [inquiries],
  );

  const selectedInquiryOption =
    inquiryOptions.find(
      (option) => String(option.value) === String(form.inquiry_id),
    ) || null;

  const closeModal = () => {
    setModal(null);
    setSelectedAttempt(null);
    setForm(emptyAttempt);
  };

  const openAddModal = () => {
    setForm({
      ...emptyAttempt,
      campus_id: campuses?.[0]?.id || "",
    });
    setModal("add");
  };

  const openEditModal = (attempt) => {
    setSelectedAttempt(attempt);
    setForm({
      inquiry_id: attempt.inquiry_id ?? "",
      campus_id: attempt.campus_id ?? campuses?.[0]?.id ?? "",
      attempt_no: attempt.attempt_no ?? 1,
      test_date: attempt.test_date ? attempt.test_date.slice(0, 10) : "",
      test_time: attempt.test_time ? attempt.test_time.slice(0, 5) : "",
      result_status: attempt.result_status ?? "",
      remarks: attempt.remarks ?? "",
    });
    setModal("edit");
  };

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const saveAttempt = async () => {
    if (!form.inquiry_id) {
      alert.error("Inquiry is required");
      return;
    }
    if (!form.campus_id) {
      alert.error("Campus is required");
      return;
    }

    const isEditing = modal === "edit";
    const payload = {
      inquiry_id: Number(form.inquiry_id),
      campus_id: Number(form.campus_id),
      attempt_no: Number(form.attempt_no),
      test_date: form.test_date || null,
      test_time: form.test_time || null,
      result_status:
        form.result_status === "" ? null : Number(form.result_status),
      remarks: form.remarks.trim() || null,
    };

    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-admission-test-attempts${
          isEditing ? `/${selectedAttempt.id}` : ""
        }`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(
          data,
          `Unable to ${isEditing ? "update" : "add"} assessment`,
        );
        return;
      }
      alert.success(
        `Assessment successfully ${isEditing ? "updated" : "added"}`,
      );
      closeModal();
      getInquiries(false);
      closeCRMModal();
    } catch (error) {
      alert.error(`Unable to ${isEditing ? "update" : "add"} assessment`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteAttempt = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-admission-test-attempts/${selectedAttempt.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authtoken}` },
        },
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to delete assessment");
        return;
      }
      alert.success(data?.payload || "Assessment successfully deleted");
      closeModal();
      getInquiries(false);
      closeCRMModal();
    } catch (error) {
      alert.error("Unable to delete assessment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      {PremissionChecker(user, "crm_admission_test_attempt.create") && (
        <button className="btn btn-primary mr-2" onClick={openAddModal}>
          Add Assessment
        </button>
      )}

      <Table className="mt-5" responsive>
        <thead>
          <tr className="text-center">
            <th>ID</th>
            <th>Attempt No</th>
            <th>Assessment Date</th>
            <th>Assessment Time</th>
            <th>Result Status</th>
            <th>Remarks</th>
            {PremissionChecker(user, "crm_admission_test_attempt.update") && (
              <th>Update</th>
            )}
            {PremissionChecker(user, "crm_admission_test_attempt.delete") && (
              <th>Delete</th>
            )}
          </tr>
        </thead>
        <tbody>
          {attempts.map((attempt) => (
            <tr className="text-center" key={attempt.id}>
              <td>{attempt?.id}</td>
              <td>{attempt?.attempt_no}</td>
              <td>{attempt?.test_date || "-"}</td>
              <td>{attempt?.test_time || "-"}</td>
              <td>{attempt?.result_status_detail?.name}</td>
              <td>{attempt?.remarks || "-"}</td>
              {PremissionChecker(user, "crm_admission_test_attempt.update") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-3"
                    onClick={() => openEditModal(attempt)}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Write.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
              {PremissionChecker(user, "crm_admission_test_attempt.delete") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-danger btn-sm mx-3"
                    onClick={() => {
                      setSelectedAttempt(attempt);
                      setModal("delete");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-danger">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Delete-user.svg",
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {loading && (
        <div className="row justify-content-center">
          <Spinner animation="border" />
        </div>
      )}
      {!loading && attempts.length === 0 && (
        <div className="text-center py-4">No assessments found.</div>
      )}

      <Modal
        show={modal === "add" || modal === "edit"}
        onHide={closeModal}
        size="lg"
      >
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            {modal === "edit" ? "Update" : "Add New"} Assessment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-12 mb-4">
              <label className="form-control-label">
                <b>
                  Inquiry <span style={{ color: "red" }}>*</span>
                </b>
              </label>
              <Select
                isDisabled
                value={selectedInquiryOption}
                options={inquiryOptions}
                onChange={(option) =>
                  setForm((current) => ({
                    ...current,
                    inquiry_id: option ? option.value : "",
                  }))
                }
                placeholder="Search inquiry by number or student name"
                isClearable
                isSearchable
              />
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>Attempt No</b>
              </label>
              <input
                disabled
                type="number"
                min="1"
                className="form-control"
                name="attempt_no"
                placeholder="Enter attempt no."
                value={form.attempt_no}
                onChange={updateField}
              />
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>Assessment Date</b>
              </label>
              <input
                type="date"
                className="form-control"
                name="test_date"
                value={form.test_date}
                onChange={updateField}
              />
            </div>

            <div className="col-md-4 mb-4">
              <label className="form-control-label">
                <b>Assessment Time</b>
              </label>
              <input
                type="time"
                className="form-control"
                name="test_time"
                value={form.test_time}
                onChange={updateField}
              />
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-control-label">
                <b>Campus</b>
              </label>
              <select
                className="form-control form-select"
                name="campus_id"
                value={form.campus_id}
                onChange={updateField}
                disabled
              >
                <option value="">Select Campus</option>
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 mb-4">
              <label className="form-control-label">
                <b>Result Status</b>
              </label>
              <select
                className="form-control form-select"
                name="result_status"
                value={form.result_status}
                onChange={updateField}
              >
                <option value="">Select Result Status</option>
                {resultStatuses.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-12 mb-4">
              <label className="form-control-label">
                <b>Remarks</b>
              </label>
              <textarea
                className="form-control"
                name="remarks"
                placeholder="Enter remarks..."
                value={form.remarks}
                onChange={updateField}
                rows="3"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn btn-primary w-100px" onClick={saveAttempt}>
              {modal === "edit" ? "Update" : "Add"}
            </button>
          )}
          <button className="btn btn-danger" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={modal === "delete"} onHide={closeModal}>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Are you sure you want to delete this assessment?
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn button_blue" onClick={deleteAttempt}>
              Delete
            </button>
          )}
          <button className="btn btn-danger btn_width" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
}

export default CRMAdmissionTestAttempts;
